import { useState, useEffect } from "react";
import PageSEO from "@/components/PageSEO";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { API } from "@/App";
import {
  Leaf, ArrowRight, ArrowLeft, Check, Lock, ChevronRight,
  Zap, IndianRupee, TrendingUp, MapPin, PlugZap, FileText,
  Loader2, AlertCircle,
} from "lucide-react";
import {
  Select as ShadSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ─── Forest palette ───────────────────────────────────────────────────────────
const F = {
  // Dark brown scale
  950: "#2d2219", 900: "#3a2e28", 800: "#504136", 700: "#5e4e41",
  600: "#6e5c4e",
  // Teal primary → bright mint highlight
  500: "#689689", 400: "#83E8BA", 300: "#8ab0a8",
  200: "#B2E6D4", 100: "#c8ede4", 50: "#edf8f4",
  // Taupe accent
  e500: "#A49E8D", e400: "#8c8678", e300: "#c0b9ac", e100: "#f0ede8",
  // Teal text/muted variants
  m600: "#2d4f4b", m500: "#3d6b65", m400: "#4a7068", m300: "#5e8480",
  // Light taupe backgrounds
  c100: "#f5f2ec", c200: "#e8e3d8", c50: "#faf9f6",
};

const INDIAN_STATES = [
  "Andhra Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
  "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh",
  "Uttarakhand","West Bengal",
];

const LAND_UNITS = [
  { value: "meters", label: "Metres (m)",  factor: 1       },
  { value: "feet",   label: "Feet (ft)",   factor: 0.3048  },
  { value: "yards",  label: "Yards (yd)",  factor: 0.9144  },
];

const CHARGER_TYPES = [
  { value: "7.4kW_AC",  label: "7.4 kW AC — Home / Retail",        est: "₹80K/unit"  },
  { value: "22kW_AC",   label: "22 kW AC — Workplace / Office",     est: "₹1.8L/unit" },
  { value: "30kW_DC",   label: "30 kW DC — Commercial Hub",         est: "₹3.5L/unit" },
  { value: "60kW_DC",   label: "60 kW DC — Fast Commercial",        est: "₹6.5L/unit" },
  { value: "120kW_DC",  label: "120 kW DC — Highway / Fleet",       est: "₹12L/unit"  },
  { value: "150kW_DC",  label: "150 kW DC — Premium Highway",       est: "₹15L/unit"  },
];

const LOCATION_TYPES = [
  { value: "urban",      label: "Urban",      desc: "City, town centre, dense residential" },
  { value: "semi-urban", label: "Semi-Urban", desc: "Suburb, small town, mixed use"         },
  { value: "highway",    label: "Highway",    desc: "National/state highway, inter-city"    },
];

function fmt(n) {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(2)}L`;
  return `₹${(n / 1000).toFixed(0)}K`;
}

// ─── Step indicator ───────────────────────────────────────────────────────────
function StepBar({ step }) {
  const steps = ["Site Details", "Generating", "Your Plan"];
  return (
    <div className="flex items-center gap-3 mb-10">
      {steps.map((label, i) => {
        const idx = i + 1;
        const done = step > idx;
        const active = step === idx;
        return (
          <div key={i} className="flex items-center gap-3 flex-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                style={done
                  ? { background: F[400], color: "#fff" }
                  : active
                    ? { background: F[500], color: "#fff", boxShadow: `0 0 0 4px ${F[500]}25` }
                    : { background: F.c200, color: F.m600 }}>
                {done ? <Check className="w-4 h-4" /> : idx}
              </div>
              <span className="text-sm font-medium hidden sm:block" style={{ color: active ? F[800] : F.m500 }}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className="flex-1 h-px" style={{ background: step > i + 1 ? F[400] : F[100] }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Field wrapper ────────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5" style={{ color: F[800] }}>{label}</label>
      {children}
    </div>
  );
}

function Input({ ...props }) {
  return (
    <input
      {...props}
      className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-all focus:ring-2"
      style={{ borderColor: F[200], background: "#fff", color: F[900], "--tw-ring-color": `${F[500]}40` }}
      onFocus={(e) => { e.currentTarget.style.borderColor = F[400]; }}
      onBlur={(e) => { e.currentTarget.style.borderColor = F[200]; }}
    />
  );
}

// GuestSelect: thin wrapper so we can apply forest palette styling to Shadcn Select
function GuestSelect({ value, onValueChange, children }) {
  return (
    <ShadSelect value={value} onValueChange={onValueChange}>
      <SelectTrigger
        className="w-full"
        style={{ borderColor: F[200], background: "#fff", color: F[900] }}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>{children}</SelectContent>
    </ShadSelect>
  );
}

// ─── Loading step ─────────────────────────────────────────────────────────────
function GeneratingStep() {
  const steps = [
    "Analysing site dimensions & shape",
    "Calculating charger placement",
    "Estimating civil & electrical costs",
    "Computing basic ROI",
    "Preparing your plan",
  ];
  const [completed, setCompleted] = useState([0]);

  useEffect(() => {
    const timers = steps.map((_, i) => {
      if (i === 0) return null;
      return setTimeout(() => setCompleted((prev) => [...prev, i]), i * 700);
    });
    return () => timers.forEach((t) => t && clearTimeout(t));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="text-center py-12">
      <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl"
        style={{ background: `${F[500]}18`, border: `2px solid ${F[200]}` }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: F[500] }} />
      </div>
      <h2 className="text-2xl font-black mb-2" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
        Generating Your Plan…
      </h2>
      <p className="text-sm mb-10" style={{ color: F.m600 }}>This usually takes under 5 seconds</p>
      <div className="max-w-sm mx-auto space-y-3">
        {steps.map((step, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.15 }}
            className="flex items-center gap-3 text-left px-4 py-2 rounded-lg"
            style={{ background: completed.includes(i) ? `${F[500]}10` : F.c200 }}>
            <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: completed.includes(i) ? F[400] : F[100] }}>
              {completed.includes(i) ? <Check className="w-3 h-3 text-white" /> : <div className="w-2 h-2 rounded-full" style={{ background: F[300] }} />}
            </div>
            <span className="text-sm" style={{ color: completed.includes(i) ? F[700] : F.m500 }}>{step}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ─── Results step ─────────────────────────────────────────────────────────────
function ResultsStep({ formData, results, onSignUp }) {
  const { cost_estimate, basic_roi, site_summary } = results;
  const costRows = [
    { label: "Charger Equipment",    value: cost_estimate.charger_equipment   },
    { label: "Civil Works",          value: cost_estimate.civil_works         },
    { label: "Electrical Infra",     value: cost_estimate.electrical_infra    },
    { label: "Solar Installation",   value: cost_estimate.solar_installation  },
    { label: "Miscellaneous (8%)",   value: cost_estimate.miscellaneous       },
  ];

  const lockedFeatures = [
    { icon: TrendingUp,  title: "Full ROI & IRR Analysis",       desc: "10-year projections, payback period, IRR, scenario sliders" },
    { icon: IndianRupee, title: "Government Subsidy Finder",      desc: `FAME III, PM e-DRIVE & ${formData.state} state schemes` },
    { icon: MapPin,      title: "Location Intelligence",          desc: "Demand score, competitor map, supply gap analysis" },
    { icon: PlugZap,     title: "Hardware Recommendations",       desc: "OEM matching, pricing & FAME eligibility for your config" },
    { icon: FileText,    title: "PDF Report Export",              desc: "Investor-ready report with all sections in one click" },
  ];

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ background: `${F[400]}20` }}>
          <Check className="w-7 h-7" style={{ color: F[400] }} />
        </div>
        <h2 className="text-2xl font-black mb-1" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
          Your EV Hub Plan Is Ready
        </h2>
        <p className="text-sm" style={{ color: F.m600 }}>
          {site_summary.charger_count}× {site_summary.charger_type.replace("_", " ")} · {site_summary.state} · {site_summary.area_sqm.toFixed(0)} m²
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* CAPEX Breakdown */}
        <div className="rounded-2xl border p-6" style={{ background: "#fff", borderColor: F[100] }}>
          <h3 className="font-bold mb-4 flex items-center gap-2" style={{ color: F[900] }}>
            <IndianRupee className="w-4 h-4" style={{ color: F[500] }} /> CAPEX Breakdown
          </h3>
          <div className="space-y-3 mb-4">
            {costRows.map((row, i) => {
              const pct = cost_estimate.total_capex > 0 ? (row.value / cost_estimate.total_capex) * 100 : 0;
              return (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span style={{ color: F.m600 }}>{row.label}</span>
                    <span className="font-semibold font-mono" style={{ color: F[800] }}>{fmt(row.value)}</span>
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: F[100] }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: F[400] }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="pt-3 flex justify-between" style={{ borderTop: `1px solid ${F[100]}` }}>
            <span className="font-bold" style={{ color: F[900] }}>Total CAPEX</span>
            <span className="font-black text-lg font-mono" style={{ color: F[500] }}>{fmt(cost_estimate.total_capex)}</span>
          </div>
        </div>

        {/* Basic ROI */}
        <div className="space-y-4">
          {[
            { label: "Monthly Revenue Est.", value: fmt(basic_roi.monthly_revenue_estimate),  sub: "at avg 10 sessions/charger/day", color: F[500]  },
            { label: "Annual Revenue Est.",   value: fmt(basic_roi.monthly_revenue_estimate * 12), sub: "before expenses",           color: F[500]  },
            { label: "Est. Payback",          value: `${basic_roi.payback_years_estimate} years`,  sub: "rough calculation",         color: F.e500  },
          ].map((item, i) => (
            <div key={i} className="rounded-xl border p-4" style={{ background: "#fff", borderColor: F[100] }}>
              <p className="text-xs mb-1" style={{ color: F.m600 }}>{item.label}</p>
              <p className="text-2xl font-black font-mono" style={{ color: item.color }}>{item.value}</p>
              <p className="text-xs mt-0.5" style={{ color: F.m500 }}>{item.sub}</p>
            </div>
          ))}
          <p className="text-xs px-1" style={{ color: F.m500 }}>
            * Rough estimates only. Actual results depend on utilisation, tariff, and local demand.
          </p>
        </div>
      </div>

      {/* Site layout preview */}
      <div className="rounded-2xl border p-4 mb-6" style={{ background: F[50], borderColor: F[100] }}>
        <h3 className="font-bold mb-3 text-sm" style={{ color: F[900] }}>Sample Layout Preview</h3>
        <div className="rounded-xl overflow-hidden relative">
          <img src="https://customer-assets.emergentagent.com/job_f60131a8-3ce8-4679-b439-ffd532e34521/artifacts/o5ipnsdl_EV_Hub%20sketch.png"
            alt="Sample EV hub layout" className="w-full opacity-60" />
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-xl"
            style={{ background: `${F[900]}88` }}>
            <Lock className="w-6 h-6 mb-2" style={{ color: F[300] }} />
            <p className="font-semibold text-sm" style={{ color: F.c100 }}>Sign up for your custom AI layout</p>
            <p className="text-xs mt-1" style={{ color: F.m300 }}>Generated specifically for your site dimensions</p>
          </div>
        </div>
      </div>

      {/* Locked premium features */}
      <div className="rounded-2xl border p-6 mb-6" style={{ background: F[900], borderColor: F[700] }}>
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-4 h-4" style={{ color: F.e300 }} />
          <p className="font-semibold text-sm" style={{ color: F.c100 }}>Unlock Full Analysis — Free with Account</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {lockedFeatures.map((feat, i) => {
            const Icon = feat.icon;
            return (
              <div key={i} className="flex items-start gap-3 rounded-xl p-3" style={{ background: F[800] }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${F[500]}25` }}>
                  <Icon className="w-4 h-4" style={{ color: F[300] }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: F.c100 }}>{feat.title}</p>
                  <p className="text-xs mt-0.5" style={{ color: F.m400 }}>{feat.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center">
        <button onClick={onSignUp}
          className="px-10 py-4 rounded-xl font-semibold text-base flex items-center gap-2 justify-center mx-auto transition-all hover:opacity-90 shadow-xl"
          style={{ background: F[500], color: F.c50, boxShadow: `0 8px 28px ${F[500]}35` }}>
          Save Plan & Unlock Full Analysis — Free
          <ArrowRight className="w-5 h-5" />
        </button>
        <p className="text-xs mt-3" style={{ color: F.m600 }}>No credit card · Takes 30 seconds with Google Sign-In</p>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function GuestDesigner() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [formData, setFormData] = useState({
    land_unit: "meters",
    land_length: "",
    land_width: "",
    state: "Karnataka",
    location_type: "urban",
    charger_type: "30kW_DC",
    charger_count: 4,
    solar_required: false,
  });

  const set = (k, v) => setFormData((prev) => ({ ...prev, [k]: v }));

  const handleGenerate = async () => {
    setError(null);
    const { land_length, land_width, charger_count, land_unit } = formData;
    if (!land_length || !land_width || !charger_count) {
      setError("Please fill in all required fields.");
      return;
    }
    const factor = LAND_UNITS.find((u) => u.value === land_unit)?.factor ?? 1;
    const lengthM = Number(land_length) * factor;
    const widthM = Number(land_width) * factor;
    setStep(2);
    try {
      const res = await fetch(`${API}/public/quick-estimate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          land_length: lengthM,
          land_width: widthM,
          charger_count: Number(charger_count),
        }),
      });
      if (!res.ok) throw new Error("Server error");
      const data = await res.json();
      setResults(data);
      setTimeout(() => setStep(3), 3500);
    } catch (e) {
      setError("Could not generate your plan. Please try again.");
      setStep(1);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: F.c100 }}>
      <PageSEO
        title="Free EV Charging Station Designer — No Sign-Up Required"
        description="Design your EV charging station layout instantly — no account needed. Enter land dimensions and charger type to get an optimised layout, CAPEX and ROI estimate for India."
        path="/try"
      />
      {/* Topbar */}
      <header className="border-b py-3 px-4 sm:px-6" style={{ background: F[900], borderColor: F[700] }}>
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: F[500] }}>
              <Leaf className="w-4 h-4" style={{ color: F.c50 }} />
            </div>
            <span className="font-black text-base" style={{ fontFamily: "Chivo, sans-serif", color: F.c100 }}>EV Hub Studio</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs px-3 py-1 rounded-full border" style={{ background: `${F[500]}20`, borderColor: `${F[500]}35`, color: F[300] }}>
              Guest Mode
            </span>
            <button onClick={login}
              className="text-sm px-4 py-1.5 rounded-lg font-medium transition-all hover:opacity-90"
              style={{ background: F.e500, color: F.c50 }}>
              Sign In Free
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 max-w-3xl">
        <StepBar step={step} />

        <AnimatePresence mode="wait">
          {/* ── STEP 1: Form ─────────────────────────────────────────── */}
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
              <div className="rounded-2xl border p-8" style={{ background: "#fff", borderColor: F[100] }}>
                <h2 className="text-2xl font-black mb-1" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
                  Tell Us About Your Site
                </h2>
                <p className="text-sm mb-8" style={{ color: F.m600 }}>We'll generate a basic layout and cost estimate in seconds. No account needed.</p>

                <div className="grid sm:grid-cols-2 gap-5 mb-5">
                  <Field label="Measurement Unit *">
                    <GuestSelect value={formData.land_unit} onValueChange={(v) => set("land_unit", v)}>
                      {LAND_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                    </GuestSelect>
                  </Field>
                  <div className="hidden sm:block" />
                  <Field label={`Land Length (${LAND_UNITS.find(u => u.value === formData.land_unit)?.label.split(" ")[1]?.replace(/[()]/g, "") || "m"}) *`}>
                    <Input type="number" placeholder="e.g. 60" value={formData.land_length} onChange={(e) => set("land_length", e.target.value)} />
                  </Field>
                  <Field label={`Land Width (${LAND_UNITS.find(u => u.value === formData.land_unit)?.label.split(" ")[1]?.replace(/[()]/g, "") || "m"}) *`}>
                    <Input type="number" placeholder="e.g. 40" value={formData.land_width} onChange={(e) => set("land_width", e.target.value)} />
                  </Field>
                  <Field label="State *">
                    <GuestSelect value={formData.state} onValueChange={(v) => set("state", v)}>
                      {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </GuestSelect>
                  </Field>
                  <Field label="Location Type *">
                    <GuestSelect value={formData.location_type} onValueChange={(v) => set("location_type", v)}>
                      {LOCATION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label} — {t.desc}</SelectItem>)}
                    </GuestSelect>
                  </Field>
                  <Field label="Charger Type *">
                    <GuestSelect value={formData.charger_type} onValueChange={(v) => set("charger_type", v)}>
                      {CHARGER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label} ({t.est})</SelectItem>)}
                    </GuestSelect>
                  </Field>
                  <Field label="Number of Chargers *">
                    <Input type="number" min={1} max={50} placeholder="e.g. 4" value={formData.charger_count} onChange={(e) => set("charger_count", e.target.value)} />
                  </Field>
                </div>

                <div className="flex items-center gap-3 mb-8 p-4 rounded-xl" style={{ background: F[50], border: `1px solid ${F[100]}` }}>
                  <input type="checkbox" id="solar" checked={formData.solar_required} onChange={(e) => set("solar_required", e.target.checked)}
                    className="w-4 h-4 accent-forest-500 cursor-pointer" style={{ accentColor: F[500] }} />
                  <label htmlFor="solar" className="text-sm cursor-pointer" style={{ color: F[800] }}>
                    Include solar installation in CAPEX estimate
                  </label>
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg mb-4" style={{ background: "#fef2f2", border: "1px solid #fecaca" }}>
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={handleGenerate}
                    className="flex-1 py-4 rounded-xl font-semibold flex items-center gap-2 justify-center transition-all hover:opacity-90 shadow-lg"
                    style={{ background: F[500], color: F.c50, boxShadow: `0 8px 24px ${F[500]}30` }}>
                    Generate My Plan <ArrowRight className="w-5 h-5" />
                  </button>
                  <button onClick={() => navigate("/")}
                    className="py-4 px-6 rounded-xl font-medium text-sm border transition-all"
                    style={{ borderColor: F[200], color: F.m600 }}>
                    Back to Home
                  </button>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm" style={{ color: F.m600 }}>
                  Want the full analysis?{" "}
                  <button onClick={login} className="font-semibold underline" style={{ color: F[500] }}>
                    Sign in free
                  </button>
                  {" "}— it takes 30 seconds and unlocks everything.
                </p>
              </div>
            </motion.div>
          )}

          {/* ── STEP 2: Generating ───────────────────────────────────── */}
          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
              <div className="rounded-2xl border p-8" style={{ background: "#fff", borderColor: F[100] }}>
                <GeneratingStep />
              </div>
            </motion.div>
          )}

          {/* ── STEP 3: Results ──────────────────────────────────────── */}
          {step === 3 && results && (
            <motion.div key="step3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              <ResultsStep formData={formData} results={results} onSignUp={login} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
